import '@ungap/with-resolvers';
import React from 'react';
import * as ReactDOM from 'react-dom/client';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Auth0Provider, AppState } from '@auth0/auth0-react';
import { loadableReady } from '@loadable/component';
import { loadSegment } from '@moda/portal-astrarium';
import { pdfjs } from 'react-pdf';
import { App } from '../App';
import { routes } from '../routers';
import { isDevelopment } from '../lib/isEnv';
import { CONFIG } from '../config';

const ReactQueryDevtools = React.lazy(async () => {
  const module = await import('@tanstack/react-query-devtools');

  return { default: module.ReactQueryDevtools };
}) as React.ComponentType<{ initialIsOpen?: boolean }>;

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // eslint-disable-next-line @typescript-eslint/no-magic-numbers
      staleTime: 5 * 60 * 1000,
      retry: 1,
      refetchOnWindowFocus: false
    }
  }
});

// PDF.js worker setup - use unpkg which has the exact version
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/legacy/build/pdf.worker.min.mjs`;

const el = document.getElementById('root');

if (document.cookie.includes('_evidon_suppress_notification_cookie')) {
  loadSegment(CONFIG.SEGMENT_WRITE_KEY, {
    integrations: {
      All: false,
      'Segment.io': true,
      'Google Analytics': true
    }
  });
} else {
  loadSegment(CONFIG.SEGMENT_WRITE_KEY, { integrations: { All: true } });
}

const router = createBrowserRouter(routes);

const onRedirectCallback = (appState?: AppState) => {
  router.navigate(appState?.returnTo || window.location.pathname, { replace: true });
};

const ClientSideApp = (
  <App>
    <QueryClientProvider client={queryClient}>
      <Auth0Provider
        domain={CONFIG.DOMAIN}
        clientId={CONFIG.API_APPLICATION_ID}
        authorizationParams={{
          redirect_uri: `${window.location.origin}/orders`,
          audience: CONFIG.AUDIENCE,
          scope: CONFIG.SCOPES
        }}
        onRedirectCallback={onRedirectCallback}
      >
        <RouterProvider router={router} />
      </Auth0Provider>
      {isDevelopment() ? (
        <React.Suspense fallback={null}>
          <ReactQueryDevtools initialIsOpen={false} />
        </React.Suspense>
      ) : null}
    </QueryClientProvider>
  </App>
);

const init = () => {
  if (!el) throw new Error('#root element not found');

  if (isDevelopment()) {
    ReactDOM.createRoot(el).render(ClientSideApp);
  } else {
    loadableReady(() => ReactDOM.createRoot(el).render(ClientSideApp));
  }
};

init();

export default init;
